<template>
    <div id="app" :class="$i18n.locale">
        <div
            v-show="hasLeftNav && !config.IS_XS && !$isSupportIframe"
            class="ticket-left-nav"
        >
            <LeftNav/>
        </div>
        <div class="ticket-content" :style="contentStyle">
            <router-view class="container"/>
        </div>
    </div>
</template>

<script setup>
import {onMounted, onUnmounted, computed} from 'vue';
import {useRoute} from 'vue-router/composables';
import LeftNav from '@/components/LeftNav.vue';
import Config from '@/config';
import {useStore} from '@/store/useStore';
import {linkClickOpen, closeSupportSkeleton} from '@/utils/supportMessage';
import {isOffcialDoc} from '@/znzs/new_src/utils/index';
import {resolveUserInfoReady} from '@/store/userInfoReady';

const config = Config;

const route = useRoute();
const store = useStore();

const pathsWithoutLeftNav = ['/ticket/detail'];
const hasLeftNav = computed(() => {
    return !pathsWithoutLeftNav.includes(route.path);
});
const contentStyle = computed(() => {
    return {
        paddingLeft: (hasLeftNav.value && !config.IS_XS && !window.isSupportIframe) ? '180px' : 0,
        height: config.IS_XS || window.isSupportIframe ? '100vh' : 'calc(100vh - 50px)',
    };
});

const linkClickListener = event => {
    const target = event.target.closest?.('a') ?? (event.target?.tagName?.toLowerCase() === 'a' ? event.target : null);
    if (target?.href) {
        event.preventDefault();
        event.stopPropagation();
        const url = target.href;
        const openNewPage = target.getAttribute('target') === '_blank'
            || isOffcialDoc(url)
            || (!!target.closest('.ernie_bot') && !target.closest('.order-list'));
        if (window.isSupportIframe) {
            linkClickOpen(url, openNewPage);
        }
        else {
            window.open(url, '_blank', 'noreferrer');
        }
    }
};

onMounted(() => {
    store.dispatch('GET_USER_BASIC_INFO', {
        features: ['rapidCreate'],
    }).finally(resolveUserInfoReady);
    document.body.addEventListener('click', linkClickListener, true);
    if (window.isSupportIframe) {
        setTimeout(() => {
            closeSupportSkeleton();
        }, 200);
    }
});

onUnmounted(() => {
    document.body.removeEventListener('click', linkClickListener, true);
});
</script>

<style lang="scss">
@import '@/common/var.scss';

#bce-content .header {
    // 顶部导航栏
    // 项目的z-index不固定，原先值为 999，存在覆盖顶部导航栏的情况
    // 这里设置为9999，保证顶部导航栏在项目的上方
    z-index: 9999;
}

#app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #2c3e50;
    background: white !important;
    // margin-top: 50px;
    height: 100%;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;

    &:hover {
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    }

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 6px;
    }

    &:hover::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
    }
}

// console 微前端的渲染逻辑会把 #app 中的内容 copy 复制到 #bce-content 中
// 复制的过程会导致 tinymce 的渲染异常，无法选中
#bce-content {
    bottom: auto !important;
}

.ticket-left-nav {
    position: fixed;
    top: 50px;
    bottom: 0;
    left: 0;
    width: 180px;
    background: white;
    box-shadow: 0px 2px 8px 0px rgb(7 12 20 / 12%);
    z-index: 3;
}

.ticket-content {
    display: flex;
    flex-direction: column;

    .container {
        flex: 1;
    }
}

// 消除智能云通用重置样式库影响
// -------- checkbox/radio
.el-checkbox__original,
.el-radio__original {
    opacity: 0 !important;
    outline: 0 !important;
    position: absolute !important;
    margin: 0 !important;
    width: 0 !important;
    height: 0 !important;
    z-index: -1 !important;
}

// notify 里的 copy 图标
.el-notification {
    .el-notification__content {
        text-align: left;
    }

    .el-icon-copy-document {
        color: $--color-primary;
        margin-left: $space-1;
        cursor: pointer;
    }
}

// 通用配置
.el-input__count {
    height: 20px;
    line-height: 20px;
}

// 弹窗
.el-dialog__footer {
    .el-button {
        min-width: 72px;
    }
}

// 超链
a, a:visited, a:hover, a:active {
    color: $--color-primary;
}

// 弹窗的基础上再 notify
.top-notify {
    z-index: 9999 !important;
}
</style>
